"use client";

import { useState } from "react";
import { SiteFooter, SiteHeader, UtilityBar } from "./components/site-chrome";

type FamilyPreset = {
  label: string;
  serverFamily: string;
  memory: string;
  storage: string;
  location: string;
  bandwidth: string;
  price: string;
};

const familyPresets: FamilyPreset[] = [
  {
    label: "E3 Family",
    serverFamily: "Intel Xeon E3-1230 V2",
    memory: "16 GB RAM",
    storage: "500 GB SSD",
    location: "Los Angeles",
    bandwidth: "1 Gbps / 20 TB Transfer",
    price: "$149.00",
  },
  {
    label: "E5 Family",
    serverFamily: "Intel Xeon E5-2640 v4",
    memory: "32 GB RAM",
    storage: "1 TB SSD",
    location: "Los Angeles",
    bandwidth: "1 Gbps / 20 TB Transfer",
    price: "$229.00",
  },
  {
    label: "EPYC Family",
    serverFamily: "AMD EPYC 7702",
    memory: "128 GB RAM",
    storage: "2 x 2 TB SSD",
    location: "Los Angeles",
    bandwidth: "1 Gbps / 20 TB Transfer",
    price: "$469.00",
  },
];

export function HomePageClient() {
  const [selectedFamilyIndex, setSelectedFamilyIndex] = useState(0);
  const selectedFamily = familyPresets[selectedFamilyIndex];

  return (
    <div className="host-sketch">
      <input className="theme-toggle-input" type="checkbox" id="theme-toggle" defaultChecked aria-label="Toggle dark mode" />
      <div className="stage">
        <SiteHeader />
        <UtilityBar />
    
        <div className="hero">
          <div>
            <h1>Dedicated servers, cloud hosting, and colocation.</h1>
            <div className="hero-copy">
              Configure servers, storage, bandwidth, and IP addresses for your applications, with 24/7 infrastructure support.
            </div>
            <div className="quiet-intro">
              <div><b>Compute</b><span>Dedicated and virtual servers with clear resource profiles and configurable options.</span></div>
              <div><b>Network</b><span>IP transit, address allocation, and bandwidth options for your deployment.</span></div>
              <div><b>Operations</b><span>Orders, invoices, servers, IPs, and support in one client area.</span></div>
            </div>
            <div className="trust-row">
              <div className="trust"><b>2006</b><span>operating history</span></div>
              <div className="trust"><b>Tier 1</b><span>network blend</span></div>
              <div className="trust"><b>IPv4</b><span>allocation support</span></div>
              <div className="trust"><b>Panel</b><span>custom console</span></div>
            </div>
          </div>
    
          <div className="builder">
            <div className="builder-head">
              <div>
                <div style={{ color: "#667085", fontSize: 12 }}>Dedicated server configurations</div>
              </div>
              <span className="live">Available</span>
            </div>
            <div className="rack">
              <div className="rack-unit"></div>
              <div className="rack-unit" style={{ marginBottom: 0 }}></div>
            </div>
            <div className="builder-body">
              <div className="tabs" role="tablist" aria-label="Dedicated server family">
                {familyPresets.map((preset, index) => (
                  <button
                    className={index === selectedFamilyIndex ? "active" : ""}
                    id={`server-family-tab-${index}`}
                    key={preset.label}
                    onClick={() => setSelectedFamilyIndex(index)}
                    onKeyDown={(event) => {
                      if (event.key === "ArrowRight") {
                        event.preventDefault();
                        setSelectedFamilyIndex((index + 1) % familyPresets.length);
                      }
                      if (event.key === "ArrowLeft") {
                        event.preventDefault();
                        setSelectedFamilyIndex((index - 1 + familyPresets.length) % familyPresets.length);
                      }
                      if (event.key === "Home") {
                        event.preventDefault();
                        setSelectedFamilyIndex(0);
                      }
                      if (event.key === "End") {
                        event.preventDefault();
                        setSelectedFamilyIndex(familyPresets.length - 1);
                      }
                    }}
                    aria-controls="server-family-panel"
                    role="tab"
                    type="button"
                    aria-selected={index === selectedFamilyIndex}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <div
                id="server-family-panel"
                aria-labelledby={`server-family-tab-${selectedFamilyIndex}`}
                role="tabpanel"
              >
                <div className="field-row"><span>Server family</span><b>{selectedFamily.serverFamily}</b></div>
                <div className="field-row"><span>Memory</span><b>{selectedFamily.memory}</b></div>
                <div className="field-row"><span>Storage</span><b>{selectedFamily.storage}</b></div>
                <div className="field-row"><span>Bandwidth</span><b>{selectedFamily.bandwidth}</b></div>
                <div className="builder-footer">
                  <div className="price"><small>Estimated monthly</small><strong>{selectedFamily.price}</strong></div>
                  <a className="btn primary" href="/contact-us">Contact Sales</a>
                </div>
              </div>
            </div>
          </div>
        </div>
    
        <div className="section service-section">
          <div className="service-showcase">
            <div className="service-visual network-focus">
              <h3>Hosting in North America and Asia</h3>
              <p>Choose a datacenter based on your users, network requirements, and preferred deployment region.</p>
              <div className="map-glow"></div>
              <img className="global-map" src="/global-network-map-optimized.jpg" alt="Global network map" />
              <div className="network-points">
                <div className="network-point"><b>Locations</b>Global facility options</div>
                <div className="network-point"><b>Routing</b>Multi-provider paths</div>
                <div className="network-point"><b>Latency</b>Closer to users</div>
              </div>
            </div>
    
            <div className="service-track">
              <div className="plan">
                <div>
                  <h3>Cloud Node</h3>
                  <p>KVM virtual servers for applications that need predictable resources.</p>
                  <div className="specs">
                    <div className="spec"><span>CPU</span><b>Shared/Dedicated</b></div>
                    <div className="spec"><span>Storage</span><b>NVMe SSD</b></div>
                    <div className="spec"><span>Deploy</span><b>Minutes</b></div>
                  </div>
                </div>
                <div className="plan-action">
                  <div className="price"><small>from</small><strong>$11</strong></div>
                  <a className="mini-btn" href="/cloud-node">See Plans</a>
                </div>
              </div>
    
              <div className="plan featured">
                <div>
                  <h3>Dedicated Server <span className="badge">Fast Deployment</span></h3>
                  <p>Bare metal capacity with configurable RAM, storage, IPs, and OS.</p>
                  <div className="specs">
                    <div className="spec"><span>CPU</span><b>Xeon / EPYC</b></div>
                    <div className="spec"><span>Network</span><b>1Gbps / 10Gbps</b></div>
                    <div className="spec"><span>Access</span><b>Full root</b></div>
                  </div>
                </div>
                <div className="plan-action">
                  <div className="price"><small>from</small><strong>$149.00</strong></div>
                  <a className="mini-btn" href="/dedicated-servers">See Plans</a>
                </div>
              </div>
    
              <div className="plan">
                <div>
                  <h3>Colocation</h3>
                  <p>Rack space, power, and network access for customer-owned hardware.</p>
                  <div className="specs">
                    <div className="spec"><span>Rack</span><b>1U to full rack</b></div>
                    <div className="spec"><span>Transit</span><b>Tier 1 blend</b></div>
                  </div>
                </div>
                <div className="plan-action">
                  <div className="price"><small>quote</small><strong>Custom</strong></div>
                  <a className="mini-btn" href="/colocation">See Plans</a>
                </div>
              </div>
            </div>
          </div>
        </div>
    
        <div className="section proof-section">
          <div className="why-panel-inner">
            <div className="why-stack">
              <div className="why-item">
                <div className="why-signal"></div>
                <div>
                  <h3>Multiple Locations</h3>
                  <p>Deploy across global datacenter locations to keep services closer to users.</p>
                </div>
              </div>
              <div className="why-item">
                <div className="why-signal"></div>
                <div>
                  <h3>Dedicated Hardware</h3>
                  <p>Choose CPU, memory, and storage configurations to match your applications.</p>
                </div>
              </div>
              <div className="why-item">
                <div className="why-signal"></div>
                <div>
                  <h3>Carrier Connectivity</h3>
                  <p>Network access through multiple transit providers.</p>
                </div>
              </div>
              <div className="why-item">
                <div className="why-signal"></div>
                <div>
                  <h3>24/7 Expert Support</h3>
                  <p>Contact support for hardware, network, and server access issues at any time.</p>
                </div>
              </div>
              <div className="why-item">
                <div className="why-signal"></div>
                <div>
                  <h3>Automated Provisioning</h3>
                  <p>Provision supported configurations through the client area after payment.</p>
                </div>
              </div>
              <div className="why-item">
                <div className="why-signal"></div>
                <div>
                  <h3>Bandwidth Options</h3>
                  <p>Select port speed and transfer capacity for your expected traffic.</p>
                </div>
              </div>
            </div>
            <div className="why-lead">
              <div>
                <div className="why-kicker">Datacenter Infrastructure</div>
                <h3>Plan your deployment.</h3>
                <p>Discuss location, power, connectivity, and support requirements with our team before ordering.</p>
              </div>
              <div className="why-metrics">
                <div className="why-metric"><b>24/7</b><span>expert support coverage</span></div>
                <div className="why-metric"><b>Tier 1</b><span>premium network blend</span></div>
              </div>
            </div>
          </div>
        </div>
    
        <div className="callback-section">
          <div className="callback-panel">
            <div className="callback-copy">
              <h2>Not sure which hosting plan is right for you?</h2>
              <p>Our infrastructure team can help compare dedicated servers, cloud nodes, bandwidth, and colocation options before you commit.</p>
              <div className="callback-actions">
                <a className="btn primary" href="/contact-us">Contact Sales</a>
                <a className="btn secondary" href="/contact-us">Contact Support</a>
              </div>
            </div>
          </div>
        </div>
    
        <SiteFooter />
      </div>
    </div>
  );
}
